import React, { useState, useCallback, useEffect } from 'react';
import { Sheet, SheetContent, SheetClose, SheetTitle } from '@/components/ui/sheet';
import { Loader2, RotateCcw, X } from 'lucide-react';
import AddPractitionerForm from '@/components/forms/AddPractitionerForm';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { practitionerSchema } from './AddPractitionerSheet';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { practitionerService } from '@/services/practitionerService';
import { toast } from '@/hooks/use-toast';
import { Practitioner } from '@/lib/types';
import { z } from 'zod';

type PractitionerFormData = z.infer<typeof practitionerSchema>;

interface ResubmitPractitionerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  clinicId: string;
  practitioner: Practitioner;
}

const ResubmitPractitionerSheet: React.FC<ResubmitPractitionerSheetProps> = ({
  open,
  onOpenChange,
  onSuccess,
  clinicId,
  practitioner,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [npiValidation, setNpiValidation] = useState<{
    isValidating: boolean;
    exists: boolean;
    practitioner?: { id: string; name: string; npi: string };
  }>({
    isValidating: false,
    exists: false,
  });

  const form = useForm<PractitionerFormData>({
    resolver: zodResolver(practitionerSchema),
    defaultValues: {
      first_name: practitioner?.first_name || '',
      last_name: practitioner?.last_name || '',
      full_name: practitioner?.full_name || '',
      email: practitioner?.email || '',
      credentials: practitioner?.credentials || '',
      license_number: practitioner?.license_number || '',
      npi_number: practitioner?.npi || '',
      dea_number: practitioner?.dea || '',
      clinic_id: clinicId,
      fax: practitioner?.fax || '',
      phone: practitioner?.phone || '',
      address_line_1: practitioner?.office_address_line_1 || '',
      address_line_2: practitioner?.office_address_line_2 || '',
      city: practitioner?.office_city || '',
      state: practitioner?.stateInfo?.code || '',
      postal_code: practitioner?.office_postal_code || '',
    },
  });

  // Update form when practitioner changes
  useEffect(() => {
    if (practitioner && open) {
      form.reset({
        first_name: practitioner?.first_name || '',
        last_name: practitioner?.last_name || '',
        full_name: practitioner?.full_name || '',
        email: practitioner?.email || '',
        credentials: practitioner?.credentials || '',
        license_number: practitioner?.license_number || '',
        npi_number: practitioner?.npi || '',
        dea_number: practitioner?.dea || '',
        clinic_id: clinicId,
        fax: practitioner?.fax || '',
        phone: practitioner?.phone || '',
        address_line_1: practitioner?.office_address_line_1 || '',
        address_line_2: practitioner?.office_address_line_2 || '',
        city: practitioner?.office_city || '',
        state: practitioner?.stateInfo?.code || '',
        postal_code: practitioner?.office_postal_code || '',
      });
    }
  }, [practitioner, open, clinicId, form]);

  const handleSubmit = async (practitionerData: PractitionerFormData) => {
    try {
      setIsSubmitting(true);

      if (npiValidation.isValidating) {
        toast({
          title: 'Please wait',
          description: 'NPI validation is in progress. Please wait a moment and try again.',
          variant: 'destructive',
        });
        return;
      }

      // Check if NPI already exists (excluding current practitioner)
      if (npiValidation.exists && npiValidation.practitioner?.id !== practitioner.id) {
        toast({
          title: 'Invalid NPI',
          description: 'A practitioner with this NPI already exists. Please use a different NPI.',
          variant: 'destructive',
        });
        return;
      }

      // Resubmit practitioner with updated data
      await practitionerService.resubmitPractitioner(clinicId, practitioner.id, {
        first_name: practitionerData.first_name,
        last_name: practitionerData.last_name,
        email: practitionerData.email,
        npi_number: practitionerData.npi_number,
        credentials: practitionerData.credentials || undefined,
        license_number: practitionerData.license_number || undefined,
        dea_number: practitionerData.dea_number || undefined,
        fax: practitionerData.fax || undefined,
        phone: practitionerData.phone || undefined,
        address_line_1: practitionerData.address_line_1,
        address_line_2: practitionerData.address_line_2 || undefined,
        city: practitionerData.city,
        state: practitionerData.state,
        postal_code: practitionerData.postal_code,

      });

      toast({
        title: 'Success',
        description: 'Practitioner application has been resubmitted successfully.',
        variant: 'success',
      });

      // Call the callback to refresh the parent component
      onSuccess();

      // Close sheet
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error resubmitting practitioner:', error);

      toast({
        title: 'Error',
        description:
          error?.response?.data?.error?.message ||
          'Failed to resubmit practitioner application. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'first_name' || name === 'last_name') {
        const firstName = value.first_name || '';
        const lastName = value.last_name || '';
        const fullName = `${firstName} ${lastName}`.trim();
        form.setValue('full_name', fullName);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Debounced NPI validation
  const validateNpi = useCallback(
    async (npi: string) => {
      if (!npi || npi.length !== 10 || !/^\d{10}$/.test(npi)) {
        setNpiValidation({ isValidating: false, exists: false });
        return;
      }

      setNpiValidation(prev => ({ ...prev, isValidating: true }));

      try {
        const result = await practitionerService.checkPractitionerNpiExists(npi);
        setNpiValidation({
          isValidating: false,
          exists: result.exists,
          practitioner: result.practitioner,
        });
      } catch (error) {
        console.error('Error validating NPI:', error);
        setNpiValidation({ isValidating: false, exists: false });
      }
    },
    []
  );

  useEffect(() => {
    const npi = form.watch('npi_number');
    const timeoutId = setTimeout(() => {
      if (npi) {
        validateNpi(npi);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [form.watch('npi_number'), validateNpi]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        className="w-full !max-w-[640px] rounded-l-2xl p-0 flex flex-col gap-0 [&>button]:hidden"
        onInteractOutside={(e) => {
          // Prevent closing when clicking on Google Places autocomplete dropdown
          const target = e.target as HTMLElement;
          if (target?.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          // Prevent closing when clicking on Google Places autocomplete dropdown
          const target = e.target as HTMLElement;
          if (target?.closest('.pac-container')) {
            e.preventDefault();
          }
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <SheetTitle className="font-semibold text-slate-900" style={{ fontSize: '1.05rem' }}>
            Resubmit Practitioner Application
          </SheetTitle>
          <SheetClose asChild>
            <button className="w-10 h-10 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 transition-colors">
              <X className="h-5 w-5 text-slate-600" />
            </button>
          </SheetClose>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          {practitioner?.rejection_reason && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm font-medium text-red-900 mb-1">Previous Rejection Reason:</p>
              <p className="text-sm text-red-800 font-semibold">{practitioner.rejection_reason}</p>
              {practitioner.rejected_at && (
                <p className="text-xs text-red-600 mt-2">
                  Rejected on: {new Date(practitioner.rejected_at).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          <Form {...form}>
            <form 
              id="resubmit-practitioner-form"
              onSubmit={form.handleSubmit(handleSubmit)} 
              className="space-y-8"
            >
              <AddPractitionerForm form={form} isLoading={isSubmitting} />
            </form>
          </Form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <Button
            variant="ghost"
            type="button"
            className="text-slate-700 hover:text-slate-900 hover:bg-transparent font-medium"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="resubmit-practitioner-form"
            className="bg-brand-teal hover:bg-brand-teal/90 rounded-lg h-11 px-8"
            disabled={isSubmitting || npiValidation.isValidating}
          >
            {isSubmitting ? (
              <div className="flex items-center space-x-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Resubmitting...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <RotateCcw className="h-4 w-4" />
                <span>Resubmit Application</span>
              </div>
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default ResubmitPractitionerSheet;
